import {
  type FunctionComponent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import FavoritePillButton from 'components/FavoriteButton/FavoritePillButton';
import SpotPreviewCardWithClarity from 'components/SpotPreviewCardWithClarity';
import { useSpotIsFavorite } from 'hooks/useSpotIsFavorite';
import type { SpotRecommendation } from 'common/api/spotRecommendations';
import type { SpotReportData } from 'types/spot';
import type { Units } from 'types/units';
import { spotReportPath } from 'utils/urls';

import { getSnapAlignStyle } from './slideUtils';
import styles from './FavoritesCarouselSnap.module.scss';

interface RecommendationSlideProps {
  index: number;
  isActive: boolean;
  isOnActivePage: boolean;
  isFirstRecommendation?: boolean;
  onFavorited?: (spotId: string) => void;
  onImpression?: (recommendation: SpotRecommendation, index: number) => void;
  onVisibleOnActivePage?: (spotId: string) => void;
  onHiddenFromActivePage?: (spotId: string) => void;
  recommendation: SpotRecommendation;
  snapPointIndexes: Set<number>;
  units: Units;
}

const stubTidePoint = { type: 'NORMAL' as const, height: 0, timestamp: 0, utcOffset: 0 };

const mapRecommendationToSpotReport = (rec: SpotRecommendation): SpotReportData => ({
  _id: rec._id,
  name: rec.name,
  conditions: rec.conditions,
  waveHeight: rec.waveHeight,
  lat: rec.lat,
  lon: rec.lon,
  cameras: rec.cameras,
  rank: rec.rank,
  wind: rec.wind,
  tide: { previous: stubTidePoint, current: stubTidePoint, next: stubTidePoint },
  timezone: rec.timezone,
  subregionName: '',
  subregionId: rec.subregionId,
});

const RecommendationSlide: FunctionComponent<RecommendationSlideProps> = ({
  index,
  isActive,
  isOnActivePage,
  isFirstRecommendation,
  onFavorited,
  onImpression,
  onVisibleOnActivePage,
  onHiddenFromActivePage,
  recommendation,
  snapPointIndexes,
  units,
}) => {
  const { asPath, push: routerPush } = useRouter();
  const href = spotReportPath({}, { _id: recommendation._id, name: recommendation.name });

  const clipPathIds = useMemo(
    () => ({
      surfBarGraphIcon: `surf-bar-graph-icon-rec-${index}`,
      windBarGraphIcon: `wind-bar-graph-icon-rec-${index}`,
    }),
    [index],
  );

  const listItemStyle = useMemo(
    () => getSnapAlignStyle(index, snapPointIndexes),
    [index, snapPointIndexes],
  );

  const spotReport = useMemo(() => mapRecommendationToSpotReport(recommendation), [recommendation]);

  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      trackEvent('Carousel Clicked', {
        carouselItem: recommendation.name,
        carouselName: 'Favorites Carousel',
        category: 'kbyg',
        path: asPath,
        postId: recommendation._id,
        positionNumber: index,
        title: 'Home',
      });
      if (!(e.target as HTMLElement).closest('a')) {
        e.preventDefault();
        routerPush(href);
      }
    },
    [recommendation, asPath, index, routerPush, href],
  );

  const isFavorited = useSpotIsFavorite(recommendation._id);
  const wasFavoritedRef = useRef(false);
  useEffect(() => {
    if (isFavorited && !wasFavoritedRef.current) {
      wasFavoritedRef.current = true;
      onFavorited?.(recommendation._id);
      trackEvent('Recommendation Favorited', {
        carouselName: 'Favorites Carousel',
        path: asPath,
        postId: recommendation._id,
        positionNumber: index,
        rank: recommendation.rank,
        source: 'recommendation',
        title: 'Home',
      });
    }
  }, [isFavorited, onFavorited, recommendation._id, recommendation.rank, asPath, index]);

  // Impression: fires once when the card first appears on the active carousel page
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    if (isOnActivePage && !impressionFiredRef.current) {
      impressionFiredRef.current = true;
      onImpression?.(recommendation, index);
    }
  }, [isOnActivePage, onImpression, recommendation, index]);

  useEffect(() => {
    if (!isActive) {
      onHiddenFromActivePage?.(recommendation._id);
      return undefined;
    }
    onVisibleOnActivePage?.(recommendation._id);
    return () => onHiddenFromActivePage?.(recommendation._id);
  }, [isActive, onVisibleOnActivePage, onHiddenFromActivePage, recommendation._id]);

  return (
    <li
      className={styles.item}
      data-testid="recommendation-carousel-snap-card"
      style={listItemStyle}
    >
      <Box className={styles.recommendationCardWrapper}>
        <SpotPreviewCardWithClarity
          cardIndex={index}
          clipPathIds={clipPathIds}
          href={href}
          layout="FULL"
          onClickHandler={handleOnClick}
          showFullscreenControl={false}
          spotReport={spotReport}
          stopStream={!isActive}
          units={units}
        />
        <Box className={styles.recommendationFavoriteAction}>
          <FavoritePillButton spotId={recommendation._id} showLabel={isFirstRecommendation} />
        </Box>
      </Box>
    </li>
  );
};

export default RecommendationSlide;
